/**
 * Erorile clientului, tipate.
 *
 * Contractul serverului e `{ error: { code, message, details? } }` cu `code` STABIL si `message`
 * scris pentru un contabil (vezi `functions/api/lib/errors.ts`). Clientul nu reformuleaza mesajul:
 * il arata asa cum a venit. Ce adauga aici e comportamentul, si el se decide din `code` + `status`,
 * niciodata din textul mesajului — un text se schimba, un cod nu.
 *
 * Trei situatii nu au voie sa fie tratate la fel, si de aceea au fiecare predicatul lor:
 *
 *   - **401 / 403** — sesiunea a expirat sau contul nu are drepturi. O reincercare automata nu
 *     poate reusi si ar consuma tacit incercarile; singurul raspuns corect e ecranul de
 *     autentificare, respectiv un mesaj de refuz.
 *   - **409** — conflict de concurenta optimista pe `version`. E contractul DOCUMENTAT al fiecarei
 *     rute care scrie: cine a citit o versiune veche reciteste randul si reaplica modificarea.
 *     A reincerca aceeasi cerere cu aceeasi `version` ar da acelasi 409 la infinit.
 *   - **retea / timeout** — cererea nu a ajuns la server, deci NU s-a scris nimic. Asta e singura
 *     categorie in care o reincercare are sens si e sigura.
 */

/** Codurile serverului, plus cele care se pot naste doar in browser. */
export type CodEroareServer =
  | 'neautentificat'
  | 'interzis'
  | 'inexistent'
  | 'date_invalide'
  | 'conflict'
  | 'imposibil'
  | 'fisier_prea_mare'
  | 'tip_neacceptat'
  | 'limita_depasita'
  /** Sesiunea e valida, dar afisarea unui secret cere re-introducerea parolei. */
  | 'necesita_reautentificare'
  /** Bindingul R2 lipseste pe mediul acesta. Nu e vina cererii si nu se repara reincercand. */
  | 'stocare_indisponibila'
  | 'eroare_interna';

export type CodEroareClient = 'retea' | 'timeout' | 'anulat' | 'raspuns_invalid';

export type CodEroare = CodEroareServer | CodEroareClient;

/** Forma exacta a plicului de eroare intors de API. */
export interface PlicEroare {
  error: { code: CodEroareServer; message: string; details?: unknown };
}

const CODURI_SERVER: ReadonlySet<string> = new Set<CodEroareServer>([
  'neautentificat',
  'interzis',
  'inexistent',
  'date_invalide',
  'conflict',
  'imposibil',
  'fisier_prea_mare',
  'tip_neacceptat',
  'limita_depasita',
  'necesita_reautentificare',
  'stocare_indisponibila',
  'eroare_interna',
]);

/** Mesajele de rezerva, cand plicul lipseste sau e stricat. Cu diacritice: le vede utilizatorul. */
const MESAJ_DUPA_STATUS: Readonly<Record<number, string>> = {
  400: 'Datele trimise nu sunt valide.',
  401: 'Sesiunea a expirat. Intră din nou în cont.',
  403: 'Nu ai drepturi pentru această operație.',
  404: 'Resursa cerută nu există.',
  409: 'Înregistrarea a fost modificată de altcineva între timp. Reîncarcă rândul și reia modificarea.',
  413: 'Fișierul depășește limita acceptată.',
  415: 'Tipul de fișier nu este acceptat.',
  422: 'Operația a fost refuzată de o regulă de business.',
  429: 'Prea multe cereri. Așteaptă câteva secunde.',
  500: 'Eroare internă. Cererea nu a fost salvată.',
  503: 'Stocarea de fișiere nu este configurată pe acest mediu. Restul aplicației funcționează.',
};

/** Traduce statusul HTTP intr-un cod stabil, cand serverul nu a apucat sa trimita plicul. */
function codDinStatus(status: number): CodEroareServer {
  switch (status) {
    case 401:
      return 'neautentificat';
    case 403:
      return 'interzis';
    case 404:
      return 'inexistent';
    case 400:
      return 'date_invalide';
    case 409:
      return 'conflict';
    case 422:
      return 'imposibil';
    case 413:
      return 'fisier_prea_mare';
    case 415:
      return 'tip_neacceptat';
    case 429:
      return 'limita_depasita';
    case 503:
      return 'stocare_indisponibila';
    default:
      return 'eroare_interna';
  }
}

export interface ContextEroare {
  /** `GET`, `PATCH`, ... — util in raportare, pentru ca acelasi cod inseamna altceva pe o scriere. */
  metoda: string;
  cale: string;
  /** `X-Request-Id`-ul trimis: se regaseste identic in `audit_log`. */
  requestId: string | null;
}

/**
 * Eroarea pe care o vede orice apelant. Poarta TOT ce trebuie ca sa se decida ceva:
 * `status` (pentru politica de reincercare), `code` (pentru comportament), `message` (pentru om),
 * `details` (pentru a atasa mesajul la campul gresit) si `requestId` (pentru corelarea cu auditul).
 */
export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly status: number;
  readonly code: CodEroare;
  readonly details: unknown;
  readonly metoda: string;
  readonly cale: string;
  readonly requestId: string | null;

  constructor(input: {
    status: number;
    code: CodEroare;
    message: string;
    details?: unknown;
    context?: Partial<ContextEroare>;
  }) {
    super(input.message);
    this.status = input.status;
    this.code = input.code;
    this.details = input.details ?? null;
    this.metoda = input.context?.metoda ?? 'GET';
    this.cale = input.context?.cale ?? '';
    this.requestId = input.context?.requestId ?? null;
  }
}

/**
 * Plicul brut -> `ApiError`.
 *
 * Se accepta si un corp care NU e plicul asteptat (proxy cazut, pagina de login HTML, corp gol):
 * in cazul acela codul se deduce din status si mesajul e cel de rezerva. Un client care ar arunca
 * o eroare de parsare in locul unui 401 ar ascunde exact informatia utila.
 */
export function eroareDinPlic(
  status: number,
  corp: unknown,
  context?: Partial<ContextEroare>,
): ApiError {
  const plic = corp as Partial<PlicEroare> | null | undefined;
  const eroare = plic?.error;

  if (eroare && typeof eroare === 'object' && typeof eroare.message === 'string') {
    const codBrut = (eroare as { code?: unknown }).code;
    const code: CodEroareServer =
      typeof codBrut === 'string' && CODURI_SERVER.has(codBrut)
        ? (codBrut as CodEroareServer)
        : codDinStatus(status);
    return new ApiError({
      status,
      code,
      message: eroare.message,
      details: (eroare as { details?: unknown }).details ?? null,
      ...(context ? { context } : {}),
    });
  }

  return new ApiError({
    status,
    code: codDinStatus(status),
    message: MESAJ_DUPA_STATUS[status] ?? `Cererea a eșuat (HTTP ${status}).`,
    details: corp ?? null,
    ...(context ? { context } : {}),
  });
}

export function esteApiError(cauza: unknown): cauza is ApiError {
  return cauza instanceof ApiError;
}

/**
 * Sesiunea a picat sau contul nu mai e activ. `Shell` arata ecranul de autentificare.
 *
 * `necesita_reautentificare` e EXCLUS deliberat, desi vine tot pe 403: sesiunea e perfect valida,
 * doar afisarea unui secret bancar cere o dovada proaspata de prezenta. Daca ar intra aici, un
 * click pe „arata parola BCR" ar da afara din aplicatie pe cineva care era autentificat.
 */
export function esteSesiuneExpirata(cauza: unknown): boolean {
  if (!esteApiError(cauza)) return false;
  if (cauza.code === 'necesita_reautentificare') return false;
  return cauza.status === 401 || cauza.status === 403;
}

/** Operatia cere re-introducerea parolei (fereastra de step-up a expirat sau nu s-a deschis). */
export function necesitaReautentificare(cauza: unknown): boolean {
  return esteApiError(cauza) && cauza.code === 'necesita_reautentificare';
}

/**
 * Conflict de concurenta optimista. Apelantul reciteste entitatea si reaplica modificarea peste
 * versiunea proaspata; valoarea tastata NU se pierde pe drum.
 */
export function esteConflict(cauza: unknown): boolean {
  return esteApiError(cauza) && cauza.status === 409;
}

/** Refuz de business (422): mesajul e deja explicatia; nu se reincearca, se corecteaza intrarea. */
export function esteRefuzDeBusiness(cauza: unknown): boolean {
  return esteApiError(cauza) && cauza.status === 422;
}

/** Cererea nu a ajuns la server, deci nu s-a scris nimic — singurul caz sigur de reincercat. */
export function esteEroareDeRetea(cauza: unknown): boolean {
  return esteApiError(cauza) && (cauza.code === 'retea' || cauza.code === 'timeout');
}

export function esteAnulata(cauza: unknown): boolean {
  return esteApiError(cauza) && cauza.code === 'anulat';
}

/**
 * Politica de reincercare pentru interogari.
 *
 * 401/403/409/422 si restul erorilor de 4xx nu se repara prin repetare. 5xx si erorile de retea,
 * da — dar de cel mult doua ori, ca sa nu se acumuleze un val de cereri peste un server deja cazut.
 */
export function reincearca(numarIncercari: number, cauza: unknown): boolean {
  if (esteAnulata(cauza)) return false;
  if (esteSesiuneExpirata(cauza) || esteConflict(cauza) || esteRefuzDeBusiness(cauza)) return false;
  // 503 din lipsa bindingului R2 e singurul 5xx care NU se repara singur: bucketul apare abia
  // dupa o activare in dashboard si un redeploy. Trei incercari ar intarzia mesajul, nu l-ar evita.
  if (esteApiError(cauza) && cauza.code === 'stocare_indisponibila') return false;
  if (necesitaReautentificare(cauza)) return false;
  // Un throttle de autentificare nu se „asteapta": reincercarea automata doar consuma din bugetul
  // de incercari si apropie blocarea, fara sa ajute pe nimeni.
  if (esteApiError(cauza) && cauza.code === 'limita_depasita') return false;
  if (esteEroareDeRetea(cauza)) return numarIncercari < 2;
  if (esteApiError(cauza)) {
    if (cauza.status === 408 || cauza.status >= 500) return numarIncercari < 2;
    return false;
  }
  return numarIncercari < 2;
}

/** Cele mai utile detalii Zod: `{ path, message }` per camp gresit. */
export interface DetaliuCamp {
  path: string;
  message: string;
}

/** Extrage detaliile de validare, ca formularul sa poata marca exact campul refuzat. */
export function detaliiCampuri(cauza: unknown): readonly DetaliuCamp[] {
  if (!esteApiError(cauza) || !Array.isArray(cauza.details)) return [];
  return (cauza.details as unknown[]).filter(
    (d): d is DetaliuCamp =>
      typeof d === 'object' &&
      d !== null &&
      typeof (d as DetaliuCamp).path === 'string' &&
      typeof (d as DetaliuCamp).message === 'string',
  );
}

/** Textul de arata omului. Cu diacritice — e singurul sir din fisierul asta care ajunge pe ecran. */
export function mesajUtilizator(cauza: unknown): string {
  if (esteApiError(cauza)) {
    if (cauza.code === 'timeout') {
      return 'Serverul nu a răspuns la timp. Cererea nu a fost salvată; încearcă din nou.';
    }
    if (cauza.code === 'retea') {
      return 'Nu am putut contacta serverul. Verifică VPN-ul; modificarea rămâne în coadă.';
    }
    return cauza.message;
  }
  return cauza instanceof Error ? cauza.message : 'Eroare necunoscută.';
}
